import React, {FC, ReactNode, useEffect, useState} from "react";
import {PostDataType, TaxonomyType} from "data/types";
import NcImage from "components/NcImage/NcImage";
import {SINGLE} from "data/single";
import SingleContent from "./SingleContent";
import {CommentType} from "components/CommentCard/CommentCard";
import SingleHeader from "./SingleHeader";
import SingleRelatedPosts from "./SingleRelatedPosts";
import {useParams} from "react-router-dom";
import {collection, doc, DocumentData, getDoc, getDocs, limit, onSnapshot, orderBy, query, startAfter, where } from "firebase/firestore";
import {auth, db} from "../../configs/firebase-config";
import {useHistory} from "react-router";
import ButtonPrimary from "../../components/Button/ButtonPrimary";
import {checkBookmark, checkPostLiked} from "../../global/Functions";

export interface PageSingleProps {
  className?: string;
}

export interface SinglePageType extends PostDataType {
  tags: TaxonomyType[];
  content: string | ReactNode;
  comments: CommentType[];
}

let lastDocument: DocumentData | undefined | null;
const PageSingle: FC<PageSingleProps> = ({ className = "" }) => {
  let commentLimit = 3;
  let comments: object[] = [];
  const history = useHistory();
  let { uid, slug } = useParams<{ uid: string, slug: string }>();
  const [post, setPost] = useState<any>(SINGLE);
  const [imageURL, setImageURL] = useState("");
  const postDocRef = doc(db, "posts", `${uid}/posts/${slug}`);
  const commentCollectionRef = collection(postDocRef, "comments");

  const calculateReadingTime = (text: string) => {
    const wpm = 225;
    const words = text.trim().split(/\s+/).length;
    return Math.ceil(words / wpm);
  }

  const nest = (items: any, id: number | null | undefined): any => {
    return items.filter((item: any) => item.parentId === id).map((item: any) => ({
      ...item,
      children: nest(items, item.id as number | null),
    }));
  };

  const getNestedComments = async (rootComment: any) => {
    const document = await getDoc(doc(commentCollectionRef, rootComment));
    if (document.get("childrens")){
      for (let childComment of document.get("childrens")){
        comments.push(await getNestedComments(childComment));
      }
    }
    return {
      docId: document.id,
      date: document.get("createdAt").toDate().toDateString(),
      like:{isLiked: false, count: document.get("heartCount")},
      ...document.data()
    };
  }

  const getAllComments = async () => {
    comments = [];
    const q = (lastDocument) ?
      query(commentCollectionRef,
      where("parentId", "==", null),
      orderBy("createdAt", "desc"),
      startAfter(lastDocument),
      limit(commentLimit))
      :
      query(commentCollectionRef,
      where("parentId", "==", null),
      orderBy("createdAt", "desc"),
      limit(commentLimit))

    const snapshot = await getDocs(q);

    for (let commentDoc of snapshot.docs){
      if (commentDoc.get("childrens")){
        for (let comment of commentDoc.get("childrens")){
          comments.push(await getNestedComments(comment));
        }
      }
      comments.push({
        docId: commentDoc.id,
        date: commentDoc.get("createdAt").toDate().toDateString(),
        like:{isLiked: false, count: commentDoc.get("heartCount")},
        ...commentDoc.data()
      })
    }

    lastDocument = snapshot.docs[snapshot.docs.length - 1];

    if (!auth.currentUser?.uid) return nest(comments, null);

    const result = await getDocs(query(collection(db, "comment-hearts"), where("postId", "==", slug)));
    if (result.empty) return nest(comments, null);

    result.docs.forEach((doc) => {
      for (let comment of comments){
        // @ts-ignore
        if (doc.get("commentId") === comment.docId){
          // @ts-ignore
          comment["like"] = {isLiked: true, count: comment.heartCount};
        }
      }
    })
    return nest(comments, null);
  }

  const handleMoreComments = async () => {
    comments = [];
    setPost({
      ...post,
      comments: [...post.comments.concat(await getAllComments())]
    })
  }

  useEffect(() => {
    setPost(SINGLE);
    const unsubscribe = onSnapshot(postDocRef,async (result) => {
      const source = result.metadata.fromCache ? "local cache" : "server";
      console.log("Post came from " + source);
      lastDocument = null;
      if (result.exists()) {
        setImageURL(result.get("image").downloadURL);
        setPost({
          id: result.id,
          source: "SINGLE",
          desc: result.get("appendix"),
          userId: result.get("userId"),
          postType: "standard",
          readingTime: calculateReadingTime(result.get("content")),
          href: `/single/${uid}/${slug}`,
          date: result.get("createdAt").toDate().toDateString(),
          featuredImage: result.get("image").downloadURL,
          like:
            {
              count: result.get("heartCount"),
              isLiked: await checkPostLiked(result.get("docID")),
            },
          bookmark:
            {
              count: 3502,
              isBookmarked: await checkBookmark(result.id)
            },
          tags: result.get("tags"),
          comments: await getAllComments(),
          ...result.data()
        })
      } else history.push("/page404");
    })
    return () => {unsubscribe()}
  }, [uid, slug]);


  return (
    <>
      <div
        className={`PageSingle pt-8 lg:pt-16 ${className}`}
        data-nc-id="PageSingle"
      >
        <header className="container rounded-xl">
          <div className="max-w-screen-md mx-auto">
            <SingleHeader pageData={post} />
          </div>
        </header>

        {
          imageURL ?
            <NcImage
              containerClassName="container my-10 sm:my-12"
              className="object-cover w-full h-full rounded-xl"
              src={imageURL}
            /> :
            <NcImage
              containerClassName="container my-10 sm:my-12"
              className="object-cover w-full h-full rounded-xl"
              src=""
            />
        }


        <div className="container">
          <SingleContent data={post} />
          {
            post.commentCount > 0 ?
              <ButtonPrimary className="dark:bg-primary-700 w-full" onClick={handleMoreComments}>
                Show More Comments
              </ButtonPrimary> :
              <></>
          }
        </div>


        {/* RELATED POSTS */}
        {
          post !== SINGLE
          ? <SingleRelatedPosts author={uid} postData={post}/>
          : <></>
        }
      </div>
    </>
  );
};

export default PageSingle;
